import { apiFetch } from "../utils/apiFetch"
import { API_BASE_URL } from "../config/apiBaseUrl"
import { User } from "../slices/authSlice"

export type TwoFactorMethod = "app" | "email"

export type TwoFactorSetup =
  | { method: "app"; secret: string; otpauthUrl: string; qrCodeDataUrl: string }
  | { method: "email"; sentTo: string }

export interface EnrollmentResult {
  backupCodes: string[]
}

// What a screen needs to set up the second step, whether the person is logged
// in or is in the middle of a login
export interface EnrollmentApi {
  start: (method: TwoFactorMethod) => Promise<TwoFactorSetup>
  finish: (method: TwoFactorMethod, code: string) => Promise<EnrollmentResult>
}

const json = (body: object) => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body)
})

export async function startTwoFactorSetup(method: TwoFactorMethod, password?: string): Promise<TwoFactorSetup> {
  return apiFetch("/api/users/me/2fa/setup", json({ method, password }))
}

// The refresh token of this session stays valid; all other sessions end
export async function enableTwoFactor(
  method: TwoFactorMethod,
  code: string,
  refreshToken: string | null
): Promise<{ backupCodes: string[]; user: User }> {
  return apiFetch("/api/users/me/2fa/enable", json({ method, code, refreshToken }))
}

// A code by e-mail to turn the e-mail method off again
export async function sendTwoFactorCode(): Promise<{ sentTo: string }> {
  return apiFetch("/api/users/me/2fa/send-code", { method: "POST" })
}

export async function disableTwoFactor(password: string, code: string): Promise<{ user: User }> {
  return apiFetch("/api/users/me/2fa/disable", json({ password, code }))
}

export interface LoginSession {
  accessToken: string
  refreshToken: string
  user: User
  backupCodes?: string[]
}

// The calls of a login are made without a session, so they bring their own error
async function loginCall(path: string, body: object) {
  let response: Response

  try {
    response = await fetch(`${API_BASE_URL}${path}`, json(body))
  } catch {
    throw new Error("Network error. Please try again.")
  }

  const data = await response.json().catch(() => ({}))

  if (!response.ok) {
    const error: any = new Error(data.message || "Login failed")
    error.expired = Boolean(data.expired)
    throw error
  }

  return data
}

export const verifyLoginCode = (twoFactorToken: string, code: string): Promise<any> =>
  loginCall("/api/users/login/2fa", { twoFactorToken, code })

export const resendLoginCode = (twoFactorToken: string): Promise<{ sentTo: string }> =>
  loginCall("/api/users/login/2fa/resend", { twoFactorToken })

// The setup of a login that may not start a session before it is done
export const loginEnrollmentApi = (twoFactorToken: string): EnrollmentApi => ({
  start: (method) => loginCall("/api/users/login/2fa/setup", { twoFactorToken, method }),
  finish: (method, code) => loginCall("/api/users/login/2fa/enable", { twoFactorToken, method, code })
})
